/**
 * renderMarkdown — kleine, veilige markdown→HTML renderer.
 *
 * SECURITY: de input wordt als EERSTE HTML-ge-escaped (& < > " '),
 * daarna pas worden markdown-patronen omgezet naar HTML-tags.
 * Hierdoor kunnen <script>-tags en andere HTML in de model-output
 * nooit als echte HTML worden geïnterpreteerd (XSS-veilig).
 *
 * Links worden alleen toegestaan voor http:// en https:// URLs
 * (javascript:-schema's en anderen worden genegeerd).
 */
export function renderMarkdown(text) {
  if (!text) return '';

  // ── 1. HTML-escape (dit is de security-grens) ─────────────────────────
  let s = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // ── 2. Fenced code blocks ```...``` ───────────────────────────────────
  // Vervangt hele blokken; inhoud is al escaped, niet opnieuw escapen.
  s = s.replace(/```[\w]*\n?([\s\S]*?)```/g, (_, code) => {
    return `<pre class="my-1 overflow-x-auto rounded bg-gray-800 px-3 py-2 text-xs text-gray-100 dark:bg-gray-900"><code>${code.trim()}</code></pre>`;
  });

  // ── 3. Inline code `x` ────────────────────────────────────────────────
  s = s.replace(/`([^`]+)`/g, (_, code) => {
    return `<code class="rounded bg-gray-200 px-1 py-0.5 font-mono text-xs text-gray-800 dark:bg-gray-700 dark:text-gray-200">${code}</code>`;
  });

  // ── 4. Bold **x** ─────────────────────────────────────────────────────
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // ── 5. Italic *x* of _x_ (niet **, pas na bold) ───────────────────────
  s = s.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  s = s.replace(/_([^_\n]+)_/g, '<em>$1</em>');

  // ── 6. Links [label](http(s)://...) — alleen http/https ──────────────
  s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_, label, url) => {
    // URL zelf is al ge-escaped dankzij stap 1; extra controle op protocol
    if (!/^https?:\/\//i.test(url)) return label;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200">${label}</a>`;
  });

  // ── 7. Headings # / ## / ### (begin van regel) ────────────────────────
  // Gebruik h3/h4 om de heading bescheiden te houden binnen een chat-bubble.
  s = s.replace(/^###\s+(.+)$/gm, '<h4 class="mt-1.5 mb-0.5 text-sm font-semibold text-gray-900 first:mt-0 dark:text-gray-100">$1</h4>');
  s = s.replace(/^##\s+(.+)$/gm,  '<h3 class="mt-1.5 mb-0.5 text-sm font-bold text-gray-900 first:mt-0 dark:text-gray-100">$1</h3>');
  s = s.replace(/^#\s+(.+)$/gm,   '<h3 class="mt-1.5 mb-0.5 text-sm font-bold text-gray-900 first:mt-0 dark:text-gray-100">$1</h3>');

  // ── 8. Unordered lists: opeenvolgende "- x" regels ────────────────────
  s = s.replace(/(^- .+(\n|$))+/gm, (block) => {
    const items = block.trim().split('\n').map(line => {
      const item = line.replace(/^- /, '').trim();
      return `<li>${item}</li>`;
    }).join('');
    return `<ul class="my-0.5 list-disc pl-5">${items}</ul>\n`;
  });

  // ── 9. Ordered lists: opeenvolgende "1. x" regels ────────────────────
  s = s.replace(/(^\d+\. .+(\n|$))+/gm, (block) => {
    const items = block.trim().split('\n').map(line => {
      const item = line.replace(/^\d+\. /, '').trim();
      return `<li>${item}</li>`;
    }).join('');
    return `<ol class="my-0.5 list-decimal pl-5">${items}</ol>\n`;
  });

  // ── 10. Paragrafen & regeleinden ──────────────────────────────────────
  // Split op lege regels; wikkel blokken die nog geen blok-tag zijn in <p>.
  const BLOCK_TAG = /^\s*<(pre|ul|ol|h[1-6]|blockquote)/;

  const paragraphs = s.split(/\n{2,}/);
  s = paragraphs.map(block => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    if (BLOCK_TAG.test(trimmed)) return trimmed;
    // Enkele regeleinden binnen een paragraaf → <br>
    return `<p class="mb-1 last:mb-0 leading-snug">${trimmed.replace(/\n/g, '<br>')}</p>`;
  }).join('\n');

  return s;
}
